import { ReactElement } from 'react';
import Svg, { Path, Circle, Rect, Line } from 'react-native-svg';
import Icon from './Icon';
import { ActivityName } from 'app/_modules/core/types';
import { ActivityType } from 'app/_modules/pathways/entities';

interface IGlyphProps {
    size: number;
    color: string;
}

const GaugeIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M4 16a8 8 0 1 1 16 0" stroke={color} strokeWidth={1.8} strokeLinecap="round" />
        <Path d="M12 16 16 11" stroke={color} strokeWidth={1.8} strokeLinecap="round" />
        <Circle cx="12" cy="16" r="1.4" fill={color} />
    </Svg>
);

const DropletIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M12 3C12 3 6 10.5 6 14.5A6 6 0 0 0 18 14.5C18 10.5 12 3 12 3Z"
            stroke={color}
            strokeWidth={1.8}
            strokeLinejoin="round"
        />
    </Svg>
);

export const HeartIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M12 20s-7-4.35-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 5c-2.5 4.65-9.5 9-9.5 9Z"
            stroke={color}
            strokeWidth={1.8}
            strokeLinejoin="round"
        />
    </Svg>
);

const ScaleIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Rect x="3" y="7" width="18" height="14" rx="3" stroke={color} strokeWidth={1.8} />
        <Circle cx="12" cy="14" r="3" stroke={color} strokeWidth={1.8} />
    </Svg>
);

const ThermometerIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M12 14.5V5a2 2 0 1 0-4 0v9.5a4 4 0 1 0 4 0Z"
            stroke={color}
            strokeWidth={1.8}
            strokeLinejoin="round"
        />
    </Svg>
);

const PulseIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M3 12h4l2-7 4 14 2-7h6"
            stroke={color}
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Svg>
);

const LungsIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M12 3v9" stroke={color} strokeWidth={1.8} strokeLinecap="round" />
        <Path
            d="M9.5 8.5C9.5 11 8 11.5 6.5 13c-1.3 1.3-2 2.7-2 4.4A2.6 2.6 0 0 0 9 19.2c.5-.6.5-1.3.5-2.1V8.5Z"
            stroke={color}
            strokeWidth={1.8}
            strokeLinejoin="round"
        />
        <Path
            d="M14.5 8.5C14.5 11 16 11.5 17.5 13c1.3 1.3 2 2.7 2 4.4A2.6 2.6 0 0 1 15 19.2c-.5-.6-.5-1.3-.5-2.1V8.5Z"
            stroke={color}
            strokeWidth={1.8}
            strokeLinejoin="round"
        />
    </Svg>
);

const ClipboardIcon = ({ size, color }: IGlyphProps) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Rect x="5" y="4" width="14" height="17" rx="2" stroke={color} strokeWidth={1.8} />
        <Rect x="9" y="2.5" width="6" height="3" rx="1" stroke={color} strokeWidth={1.8} />
        <Line x1="8" y1="10" x2="16" y2="10" stroke={color} strokeWidth={1.6} strokeLinecap="round" />
        <Line x1="8" y1="14" x2="16" y2="14" stroke={color} strokeWidth={1.6} strokeLinecap="round" />
    </Svg>
);

export const WalkIcon = ({ size = 24, color }: { size?: number; color: string }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Circle cx="13" cy="4.3" r="2.1" fill={color} />
        <Path d="M13 8.6v4.4l-2.9 5.4" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
        <Path d="M13 13l3 3 1 3.2" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
        <Path d="M9.2 10.8 13 9.3l3.4 1.4" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
);

export const LockIcon = ({ size = 14, color }: { size?: number; color: string }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Rect x="5" y="11" width="14" height="9" rx="2" stroke={color} strokeWidth={1.8} />
        <Path d="M8 11V8a4 4 0 0 1 8 0v3" stroke={color} strokeWidth={1.8} strokeLinecap="round" />
    </Svg>
);

const VITAL_ICONS: Partial<Record<ActivityName, (props: IGlyphProps) => ReactElement>> = {
    [ActivityName.BLOOD_PRESSURE]: GaugeIcon,
    [ActivityName.SPO2]: DropletIcon,
    [ActivityName.HEART_RATE]: HeartIcon,
    [ActivityName.WEIGHT]: ScaleIcon,
    [ActivityName.TEMPERATURE]: ThermometerIcon,
    [ActivityName.ECG]: PulseIcon,
    [ActivityName.DYSPNEA]: PulseIcon,
    [ActivityName.STEP]: WalkIcon,
    [ActivityName.SPIROMETRY]: LungsIcon,
};

interface IActivityIconProps {
    activityType: string;
    activityName: string;
    size?: number;
    color: string;
}

export const ActivityIcon = ({ activityType, activityName, size = 20, color }: IActivityIconProps) => {
    if (activityType === ActivityType.PATHWAY_DRUG) {
        return <Icon name="medicine-box" size={size} color={color} />;
    }
    if (activityType === ActivityType.PATHWAY_QUESTIONNAIRE) {
        return <ClipboardIcon size={size} color={color} />;
    }
    const VitalGlyph = VITAL_ICONS[activityName as ActivityName];
    if (VitalGlyph) return <VitalGlyph size={size} color={color} />;
    return <Icon name="check-circle" size={size} color={color} />;
};

export default ActivityIcon;
